import {FiCopy, FiTrash2} from "react-icons/fi";

import {FormLabel} from "@/components/form/FormLabel.tsx";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";
import {IconButton} from "@/components/iconButton/IconButton.tsx";
import {Select} from "@/components/select/Select.tsx";
import {SelectOption} from "@/components/select/Select.types.ts";
import {CheckInfo} from "@/lib/queries/statuspages.ts";
import {METRIC_AGGREGATIONS, MetricAggregation, MetricsExplorerSchema} from "@/lib/types/metricsExplorer.ts";
import {ExplorerSeriesConfig} from "@/pages/metricsExplorer/explorerConfig.ts";
import {
  availableDataTypes,
  availableDimensions,
  availableMetrics,
  findDataType,
  pruneSelections,
} from "@/pages/metricsExplorer/schemaUtils.ts";
import {CheckMultiSelect} from "@/pages/metricsExplorer/seriesBuilder/CheckMultiSelect.tsx";
import {DimensionFilterSelect} from "@/pages/metricsExplorer/seriesBuilder/DimensionFilterSelect.tsx";

import styles from "./SeriesSpecRow.module.scss";

interface SeriesSpecRowProps {
  index: number;
  series: ExplorerSeriesConfig;
  schema: MetricsExplorerSchema | undefined;
  checkTypes: string[];
  start: Date;
  end: Date;
  onChange: (patch: Partial<ExplorerSeriesConfig>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onChecksResolved: (checks: CheckInfo[]) => void;
}

const AGGREGATION_LABELS: Record<MetricAggregation, string> = {
  AVG: "Average",
  MIN: "Minimum",
  MAX: "Maximum",
  SUM: "Sum",
  COUNT: "Count",
  MEDIAN: "Median",
  P75: "75th percentile",
  P90: "90th percentile",
  P95: "95th percentile",
  P99: "99th percentile",
};

const toOption = (value: string, label: string): SelectOption => ({value, label});

export const SeriesSpecRow = ({
  index,
  series,
  schema,
  checkTypes,
  start,
  end,
  onChange,
  onRemove,
  onDuplicate,
  onChecksResolved,
}: SeriesSpecRowProps) => {
  const dataTypes = availableDataTypes(schema, checkTypes);
  const selectedDataType = findDataType(schema, checkTypes, series.dataType);
  const metrics = availableMetrics(selectedDataType);
  const dimensions = availableDimensions(selectedDataType);
  const mixedCheckTypes = checkTypes.length > 1 && dataTypes.length === 0;

  // Splitting by a dimension implies wanting to choose which of its values are drawn, so
  // those come first; below them sit the dimensions filtered without being split on.
  const filterOnlyDimensions = Object.keys(series.filters).filter((name) => !series.groupBy.includes(name));
  const filterableDimensions = [...series.groupBy, ...filterOnlyDimensions];
  const fieldId = (field: string) => `series-${series.ref}-${field}`;
  const dimensionLabel = (name: string) => dimensions.find((dimension) => dimension.name === name)?.label ?? name;

  const handleChecksChange = (checkIds: number[]) => {
    onChange({checkIds});
  };

  const handleFilterDimensionsChange = (names: string[]) => {
    // The split-by dimensions' own values are not this select's to drop; only the rows
    // added here are rebuilt, and one that survives keeps what was already picked.
    const next: Record<string, string[]> = {};
    series.groupBy.forEach((name) => {
      if (series.filters[name]) {
        next[name] = series.filters[name];
      }
    });
    names.forEach((name) => {
      next[name] = series.filters[name] ?? [];
    });
    onChange({filters: next});
  };

  const handleFilterValuesChange = (dimension: string, values: string[]) => {
    onChange({filters: {...series.filters, [dimension]: values}});
  };

  const handleDataTypeChange = (dataTypeName: string) => {
    // Metrics and dimensions belong to a table; carrying them across would build a query
    // the backend rejects.
    const next = findDataType(schema, checkTypes, dataTypeName);
    const pruned = pruneSelections(next, series.metrics, series.groupBy, series.filters);
    onChange({dataType: dataTypeName, ...pruned});
  };

  return (
    <div className={styles.row}>
      <div className={styles.header}>
        <span className={styles.title}>Series {index + 1}</span>
        <div className={styles.actions}>
          <IconButton icon={FiCopy} title="Duplicate series" onClick={onDuplicate} />
          <IconButton icon={FiTrash2} title="Remove series" onClick={onRemove} />
        </div>
      </div>

      <div>
        <FormLabel forId={fieldId("checks")}>Checks</FormLabel>
        <CheckMultiSelect
          inputId={fieldId("checks")}
          value={series.checkIds}
          onChange={handleChecksChange}
          onChecksResolved={onChecksResolved}
        />
        {mixedCheckTypes && (
          <FormSubtext error="These check types collect no metric in common. Put them in separate series." />
        )}
      </div>

      <div className={styles.fields}>
        <div>
          <FormLabel forId={fieldId("data-type")}>Data</FormLabel>
          <Select
            inputId={fieldId("data-type")}
            size="small"
            options={dataTypes.map((dataType) => toOption(dataType.name, dataType.name))}
            value={series.dataType ? toOption(series.dataType, series.dataType) : null}
            onChange={(option) => handleDataTypeChange((option as SelectOption | null)?.value ?? "")}
            isDisabled={dataTypes.length === 0}
            placeholder={checkTypes.length === 0 ? "Select a check first" : "Select data…"}
          />
        </div>

        <div>
          <FormLabel forId={fieldId("aggregation")}>Aggregation</FormLabel>
          <Select
            inputId={fieldId("aggregation")}
            size="small"
            options={METRIC_AGGREGATIONS.map((value) => toOption(value, AGGREGATION_LABELS[value]))}
            value={toOption(series.aggregation, AGGREGATION_LABELS[series.aggregation])}
            onChange={(option) =>
              onChange({aggregation: ((option as SelectOption | null)?.value ?? "AVG") as MetricAggregation})
            }
          />
        </div>

        <div className={styles.fullWidth}>
          <FormLabel forId={fieldId("metrics")}>Metrics</FormLabel>
          <Select<SelectOption, true>
            inputId={fieldId("metrics")}
            isMulti
            size="small"
            options={metrics.map((metric) => toOption(metric.name, metric.label))}
            value={series.metrics.map((name) =>
              toOption(name, metrics.find((metric) => metric.name === name)?.label ?? name)
            )}
            onChange={(options) => onChange({metrics: [...(options ?? [])].map((option) => option.value)})}
            isDisabled={metrics.length === 0}
            placeholder="Select metrics…"
          />
        </div>

        <div className={styles.fullWidth}>
          <FormLabel forId={fieldId("group-by")}>Split by</FormLabel>
          <Select<SelectOption, true>
            inputId={fieldId("group-by")}
            isMulti
            size="small"
            options={dimensions.map((dimension) => toOption(dimension.name, dimension.label))}
            value={series.groupBy.map((name) =>
              toOption(name, dimensions.find((dimension) => dimension.name === name)?.label ?? name)
            )}
            onChange={(options) => onChange({groupBy: [...(options ?? [])].map((option) => option.value)})}
            isDisabled={dimensions.length === 0}
            placeholder="Don't split"
          />
          <FormSubtext subText="One line per value, e.g. per location or per step." />
        </div>

        <div className={styles.fullWidth}>
          <FormLabel forId={fieldId("filter-by")}>Filter by</FormLabel>
          <Select<SelectOption, true>
            inputId={fieldId("filter-by")}
            isMulti
            size="small"
            options={dimensions
              .filter((dimension) => !series.groupBy.includes(dimension.name))
              .map((dimension) => toOption(dimension.name, dimension.label))}
            value={filterOnlyDimensions.map((name) => toOption(name, dimensionLabel(name)))}
            onChange={(options) => handleFilterDimensionsChange([...(options ?? [])].map((option) => option.value))}
            isDisabled={dimensions.length === 0}
            placeholder="Filter on another dimension"
          />
          <FormSubtext subText="Narrows the series without splitting it." />
        </div>

        {filterableDimensions.length > 0 && (
          <div className={styles.filters}>
            {filterableDimensions.map((name) => (
              <DimensionFilterSelect
                key={name}
                fieldId={fieldId(`filter-${name}`)}
                checkIds={series.checkIds}
                dataType={series.dataType}
                dimension={name}
                label={dimensionLabel(name)}
                value={series.filters[name] ?? []}
                start={start}
                end={end}
                onChange={(values) => handleFilterValuesChange(name, values)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
